<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <div class="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-4">
        <span class="summary-chip">
          {{ $t(`${i18nScope}.summary_opening`) }}: ${{ formatAmount(summary.openingBalance) }}
        </span>
        <span class="summary-chip">
          {{ $t(`${i18nScope}.summary_closing`) }}: ${{ formatAmount(summary.closingBalance) }}
        </span>
        <span class="summary-chip" :class="Number(summary.difference || 0) >= 0 ? 'chip-success' : 'chip-danger'">
          {{ $t(`${i18nScope}.summary_difference`) }}: ${{ formatAmount(summary.difference) }}
        </span>
      </div>

      <a-table
        :columns="columns"
        :data-source="items"
        :row-key="'id'"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1450 }"
        class="text-nowrap align-middle register-history-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ $t(`${i18nScope}.no_register_history_found`) }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'user'">
            {{ getUserName(record.user) }}
          </template>

          <template v-else-if="column && column.key === 'opened_at'">
            {{ formatDate(record.opened_at) }}
          </template>

          <template v-else-if="column && column.key === 'closed_at'">
            {{ record.closed_at ? formatDate(record.closed_at) : "-" }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="Number(record.status) === 1 ? 'status-open' : 'status-closed'">
              {{ Number(record.status) === 1 ? $t(`${i18nScope}.status_open`) : $t(`${i18nScope}.status_closed`) }}
            </span>
          </template>

          <template v-else-if="['opening_balance','payments_cash','payments_bank','subtotal','closing_balance','difference'].includes(String(column?.key || ''))">
            <span :class="column && column.key === 'difference' ? getDifferenceClass(record.difference) : ''">
              ${{ formatAmount((record as Record<string, unknown>)[String(column?.key)]) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'close_note'">
            {{ record.close_note || "-" }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";
import type { PosRegister } from "@/services/register";

export default defineComponent({
  name: "RegisterHistory",
  props: {
    items: {
      type: Array as () => PosRegister[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    summary: {
      type: Object as () => { openingBalance: number; closingBalance: number; difference: number },
      required: true,
    },
    i18nScope: {
      type: String,
      default: "register_history_report_page",
    },
  },
  emits: ["table-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const columns = computed(() => [
      { title: t(`${props.i18nScope}.table_user`), key: "user", width: 180, align: "center" as const },
      { title: t(`${props.i18nScope}.table_opened_at`), key: "opened_at", dataIndex: "opened_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_closed_at`), key: "closed_at", dataIndex: "closed_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_opening_balance`), key: "opening_balance", dataIndex: "opening_balance", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_cash`), key: "payments_cash", dataIndex: "payments_cash", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_bank`), key: "payments_bank", dataIndex: "payments_bank", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_total`), key: "subtotal", dataIndex: "subtotal", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_closing_balance`), key: "closing_balance", dataIndex: "closing_balance", width: 170, align: "center" as const },
      { title: t(`${props.i18nScope}.table_difference`), key: "difference", dataIndex: "difference", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_close_note`), key: "close_note", dataIndex: "close_note", width: 220, align: "center" as const },
    ]);

    const formatDate = (dateString?: string | null) => {
      if (!dateString) return t(`${props.i18nScope}.not_available`);
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatAmount = (value?: unknown) =>
      Number(value ?? 0).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const getUserName = (user?: PosRegister["user"]) => {
      if (!user) return "-";
      const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
      return fullName || user.username || user.email || "-";
    };

    const getDifferenceClass = (value?: number) => (Number(value ?? 0) >= 0 ? "text-success" : "text-danger");

    const handleChange = (pagination: TablePaginationConfig) => {
      emit("table-change", pagination.current || 1, pagination.pageSize || 10);
    };

    return {
      columns,
      formatDate,
      formatAmount,
      getUserName,
      getDifferenceClass,
      handleChange,
    };
  },
});
</script>

<style scoped>
.register-history-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.register-history-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #5d5fef;
  background: rgba(93, 95, 239, 0.1);
}

.chip-success {
  color: #1f9254;
  background: #e8f7ee;
}

.chip-danger {
  color: #d33c43;
  background: #fdecec;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 90px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-open {
  color: #1f9254;
  background: #e8f7ee;
}

.status-closed {
  color: #61748a;
  background: #eef2f7;
}
</style>
